import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useQueryGetFlexHotGameList from '@/modules/settings/api/hooks/useQueryGetFlexHotGameList'
import { forwardRef } from 'react'
import { TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { HotGameFlexMessageModel } from '@/modules/settings/types'

interface FlexMessageSelectorProps {
	value?: string
	onChange?: (value: string) => void
	disabled?: boolean
}

export const FlexMessageSelector = forwardRef<
	HTMLButtonElement,
	FlexMessageSelectorProps
>(({ value, onChange, disabled = false }, ref) => {
	// Fetch only active flex messages
	const { data: flexMessagesResponse, isLoading } = useQueryGetFlexHotGameList({
		isActive: true,
	})

	const flexMessages = flexMessagesResponse?.data?.items || []
	const isEmpty = !isLoading && flexMessages.length === 0

	return (
		<div className="space-y-3">
			{isEmpty && (
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
					<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
					<div className="text-amber-800 dark:text-amber-200">
						<p className="font-medium">ยังไม่มี Flex Message ที่ใช้งานได้</p>
						<p className="mt-0.5">
							กรุณาสร้าง Flex Message ก่อนใช้งานช่องทางนี้ โดยสร้างได้ที่หน้า{' '}
							<Link
								to="/operator/settings/flex-hot-games"
								className="font-medium underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-100"
							>
								จัดการ Flex Message
							</Link>
						</p>
					</div>
				</div>
			)}
			<Select
				value={value}
				onValueChange={onChange}
				disabled={disabled || isLoading || isEmpty}
			>
				<SelectTrigger ref={ref}>
					<SelectValue
						placeholder={
							isLoading ? 'กำลังโหลด...' : 'เลือก Flex Message ที่ต้องการส่ง'
						}
					/>
				</SelectTrigger>
				<SelectContent>
					{flexMessages && flexMessages.length > 0 ? (
						flexMessages.map((flexMessage: HotGameFlexMessageModel) => (
							<SelectItem key={flexMessage.id} value={flexMessage.id}>
								{flexMessage.name} ({flexMessage.games?.length || 0} เกม)
							</SelectItem>
						))
					) : (
						<div className="px-2 py-6 text-center text-sm text-muted-foreground">
							ไม่พบ Flex Message ที่ใช้งานได้
						</div>
					)}
				</SelectContent>
			</Select>
		</div>
	)
})

FlexMessageSelector.displayName = 'FlexMessageSelector'
